import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, AbstractControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Actions, ofType } from '@ngrx/effects';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { accessSelector } from 'src/app/global/store/global.selectors';
import { AccessControlService } from 'src/app/global/services/access-control/access-control.abstract.service';
import { Role, BatchAssignPayload, BatchRemovePayload } from 'src/app/global/services/access-control/access-control.model';

@Component({
    selector: 'app-user-role-modal',
    templateUrl: './user-role-modal.component.html',
    styleUrls: ['./user-role-modal.component.scss'],
})
export class UserRoleModalComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    public userRoleForm: FormGroup;

    public t: any = false;

    public user;

    constructor(
        private formBuilder: FormBuilder,
        private store: Store<State>,
        private actions$: Actions,
        private messageService: MessageService,
        private translateService: TranslateService,
        private accessControlService: AccessControlService,
        public dialogRef: MatDialogRef<UserRoleModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        this.user = data.user;
    }

    get role(): AbstractControl {
        return this.userRoleForm.get('role');
    }

    public roleOptions: Role[] = [];
    public userInitialRole: Role[] = [];

    ngOnInit(): void {
        this.store
            .select(accessSelector)
            .pipe(
                takeUntil(this.destroy$),
                map((accesses) => {
                    accesses.forEach((access) =>
                        this.roleOptions.push({
                            name: access.title,
                            id: access?._id,
                            userId: this.user.id,
                            selected: false,
                        }),
                    );
                }),
            )
            .subscribe();

        this.accessControlService
            .getUserRole(this.user.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    response.forEach((res) => {
                        (this.userInitialRole.push({
                            name: res.title,
                            id: res?._id,
                            userId: this.user.id,
                            selected: true,
                        }),
                            this.roleOptions.forEach((item) => {
                                if (res?._id === item.id) {
                                    item.selected = true;
                                }
                            }));
                    });
                },
                error: (error) => {
                    console.error('Error assigning role:', error);
                    this.messageService.error(error.message || 'Error assigning role');
                },
            });

        this.userRoleForm = this.formBuilder.group(
            {
                role: new FormControl('', [Validators.required]),
            },
            {
                updateOn: 'blur',
            },
        );

        this.role.setValue(this.user.access || '');

        this.translateService
            .get('common.userRole')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));
    }

    // Add this method to handle role selection changes from the child
    onRoleSelectionChanged(selectedRoles: Role[]): void {
        // Update roleOptions' selected state based on the selectedRoles array
        this.roleOptions.forEach((role) => {
            role.selected = selectedRoles.some((selected) => selected.id === role.id);
        });
    }

    save(): void {
        if (this.roleOptions.length > 0) {
            // Roles to assign (newly selected roles)
            const rolesToAssign = this.roleOptions
                .filter((element) => !this.userInitialRole.some((initialRole) => initialRole.id === element.id))
                .filter((element) => element.selected)
                .map((element) => ({
                    roleId: element.id,
                    userId: element.userId,
                }));

            // Roles to remove (previously selected roles that are now unselected)
            const rolesToRemove = this.userInitialRole
                .filter((initialRole) => this.roleOptions.some((option) => option.id === initialRole.id && !option.selected))
                .map((element) => ({
                    roleId: element.id,
                    userId: element.userId,
                }));

            // Handle role assignments
            if (rolesToAssign.length > 0) {
                const batchPayload: BatchAssignPayload = {
                    assignments: rolesToAssign,
                };

                this.accessControlService
                    .assignRoles(batchPayload)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.messageService.success(this.translateService.instant('common.success'));
                            this.closeModal();
                        },
                        error: (error) => {
                            console.error('Error assigning roles:', error);
                            this.messageService.error(error.message || 'Error assigning roles');
                        },
                    });
            }

            // Handle role removals (assuming you have a removeRoles method)
            if (rolesToRemove.length > 0) {
                const removeBatchPayload: BatchRemovePayload = {
                    removals: rolesToRemove,
                };

                this.accessControlService
                    .removeRoles(removeBatchPayload)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.messageService.success(this.translateService.instant('common.success'));
                            this.closeModal();
                        },
                        error: (error) => {
                            console.error('Error removing roles:', error);
                            this.messageService.error(error.message || 'Error removing roles');
                        },
                    });
            }

            // If no changes, just close the modal
            if (rolesToAssign.length === 0 && rolesToRemove.length === 0) {
                this.closeModal();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    cancel(): void {
        this.dialogRef.close();
    }

    closeModal(): void {
        this.dialogRef.close();
    }
}
